import { useState } from 'react';
import { Input, Modal, ModalBody, ModalHeader, Row, Col } from 'reactstrap';
import { ArrowRight, X } from 'react-feather';
import { Btn, H4, Image } from '../../AbstractElements';
import { dynamicImage } from '../../service';
import { useTranslation } from 'react-i18next';

interface InviteSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendInvite?: (firstName: string, lastName: string, email: string) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface FormErrors {
  firstName?: string;
  email?: string;
}

const InviteSignupModal = ({ isOpen, onClose, onSendInvite }: InviteSignupModalProps) => {
  const { t } = useTranslation();
  
   const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('FirstNameRequired') || 'First name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = t('EmailRequired') || 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('InvalidEmail') || 'Invalid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

   const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
     if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSendInvite = () => {
    if (validateForm()) {
      if (onSendInvite) {
        onSendInvite(formData.firstName, formData.lastName, formData.email);
      }
      
       setFormData({
        firstName: '',
        lastName: '',
        email: ''
      });
      setErrors({});
      onClose();
    }
  };

  const isFormValid = formData.firstName.trim() !== '' && formData.email.trim() !== '';

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" backdrop="static">
      <ModalHeader className="border-0 position-relative">
        <H4 className="fw-semibold">Send Invitation</H4>
        <Btn
          className="btn-md p-1 rounded-circle border-0 position-absolute"
          style={{ top: '15px', right: '15px' }}
          onClick={onClose}
        >
          <X size={19} />
        </Btn>
      </ModalHeader>
      <ModalBody>
        <div className="text-center">
          <div>
            <Image
              src={dynamicImage(`gif/email-invite.gif`)}
              alt="email-invite"
              className="img-fluid"
              style={{ maxHeight: '150px', marginTop: '-45px' }}
            />
          </div>
          <p className="text-muted fs-6" style={{ marginTop: '-20px', marginBottom: '30px' }}>
            Invite a new user to sign up by entering their details below. They will receive a personalized invitation link via email.
          </p>
        </div>

        <div className="mx-auto" style={{ maxWidth: '720px' }}>
          <Row className="g-3 mb-3">
            <Col sm="6">
              <label className="form-label fw-medium mb-1">
                {t('FirstName')} <span className="txt-danger">*</span>
              </label>
              <Input
                type="text"
                className="form-control form-control-md"
                placeholder={t('EnterFirstName')}
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '10px 14px',
                }}
              />
              {errors.firstName && (
                <span className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {errors.firstName}
                </span>
              )}
            </Col>
            <Col sm="6">
              <label className="form-label fw-medium mb-1">
                {t('LastName')}
              </label>
              <Input
                type="text"
                className="form-control form-control-md"
                placeholder={t('EnterLastName')}
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '10px 14px',
                }}
              />
            </Col>
          </Row>

          <div className="mb-3">
            <label className="form-label fw-medium mb-1">
              {t('Email')} <span className="txt-danger">*</span>
            </label>
            <Input
              type="email"
              className="form-control form-control-md"
              placeholder={t('EnterEmail')}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '10px 14px',
              }}
            />
            {errors.email && (
              <span className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                {errors.email}
              </span>
            )}
          </div>

          <div className="d-flex justify-content-end mt-4">
            <Btn
              color="primary"
              className="d-flex align-items-center gap-2"
              onClick={handleSendInvite}
              disabled={!isFormValid}
              style={{
                borderRadius: '8px',
                padding: '8px 20px',
                fontWeight: '500',
              }}
            >
              {t("SendInvite")}
              <ArrowRight size={16} />
            </Btn>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default InviteSignupModal;